import { collection, documentId, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";

export async function whatsappTemplateMessage(companyId: string): Promise<any> {

    try {
        // get phoneNNumber and access tocken from database
        const accessTokenQuery = query(
            collection(db, "whatsappBusinessCloudAccounts"),
            where("companyId", "==", companyId),
        )

        const accessTokenSnapshot = await getDocs(accessTokenQuery);
        if (accessTokenSnapshot.empty) {
            console.error("No WhatsApp Business Cloud account found for this company");
            return;
        }

        const accessTokenDoc = accessTokenSnapshot.docs[0];
        const accessTokenData = accessTokenDoc.data();
        const accessToken = accessTokenData.accountAccessToken;
        const whatsappAccountId = accessTokenDoc.id;

        console.log("WhatsApp Account ID:", whatsappAccountId);
        console.log("Access Token:", accessToken);

        // Now navigate to the businesses subcollection of this document
        const businessesCollectionRef = collection(
            db,
            "whatsappBusinessCloudAccounts",
            whatsappAccountId,
            "businesses"
        );

        // Create a query with a where clause
        const q = query(
            businessesCollectionRef,
            where("companyId", "==", companyId)
        );

        // Execute the query
        const businessesSnapshot = await getDocs(q);

        if (businessesSnapshot.empty) {
            console.log("No businesses found with the specified company ID");
            return [];
        }

        // Get the first business document (or you can loop through all businesses if needed)
        const businessDoc = businessesSnapshot.docs[0];
        const businessId = businessDoc.id;
        const businessData = businessDoc.data();

        console.log("Business ID:", businessId);
        console.log("Business Data:", businessData);

        // Now navigate to the wabaAccounts subcollection of this business document
        const wabaAccountsCollectionRef = collection(
            db,
            "whatsappBusinessCloudAccounts",
            whatsappAccountId,
            "businesses",
            businessId,
            "wabaAccounts"
        );

        // Create a query with a where clause
        const wabaAccountQuery = query(
            wabaAccountsCollectionRef,
            where("companyId", "==", companyId)
        );

        // Execute the query
        const wabaAccountsSnapshot = await getDocs(wabaAccountQuery);
        if (wabaAccountsSnapshot.empty) {
            console.log("No WABA accounts found with the specified company ID");
            return [];
        }

        // Get the first WABA account document (or you can loop through all if needed)
        const wabaAccountDoc = wabaAccountsSnapshot.docs[0];
        const wabaAccountId = wabaAccountDoc.id;
        const wabaAccountData = wabaAccountDoc.data();

        console.log("WABA Account ID:", wabaAccountId);
        console.log("WABA Account Data:", wabaAccountData);

        // get all the templates 
        const baseUrl = `https://graph.facebook.com/v20.0/${wabaAccountId}/message_templates`;

        // Add query parameters
        const url = new URL(baseUrl);
        url.searchParams.append('access_token', accessToken);

        // Make the request
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        // Check if the request was successful
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
        }

        // Parse and return the response data
        const data = await response.json();
        console.log("Templates data:", data);
        return data;
    } catch (error) {
        console.error('Error fetching message templates:', error);
        throw error;
    }
}


/**
 * Types for WhatsApp Templates
 */
interface Variable {
    fullVar: string;
    // Add any other properties needed
}

interface ComponentState {
    type: string;
    variables?: Variable[];
    // Add any other properties needed
}

// Template types based on WhatsApp Business API
type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
type TemplateStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
type TemplateType = 'TEXT' | 'MEDIA' | 'INTERACTIVE';
type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

// Template component interface
interface TemplateComponent {
    type: ComponentType;
    text?: string;
    format?: string;
    example?: {
        header_text?: string[];
        body_text?: string[][];
        header_handle?: string[];
    };
    uploadedFile?: File;
    uploadedFileUrl?: string;
    isUploading?: boolean;
    uploadError?: string;
    uploadedHandle?: string;
}

// Template interface
interface WhatsappTemplate {
    id?: string;
    name: string;
    category: TemplateCategory;
    language: string;
    status?: TemplateStatus;
    components: TemplateComponent[];
    parameter_format?: string;
    createdAt?: any;
    updatedAt?: any;
}

interface PhoneNumberInfo {
    numberId: string;
    // Add any other phone number info properties needed
}

interface DisplayFields {
    headerText: string | null;
    body: string;
    footer: string;
}

interface SendTemplateParams {
    selectedContact: string[];
    selectedPhoneNumberInfo: PhoneNumberInfo;
    companyId: string;
    template: WhatsappTemplate;
    inputValues: Record<string, string>;
    componentsState: ComponentState[];
    uploadedMediaId: string | null;
    selectedFile: File | null;
    accessToken?: string;
    fetchAccessToken: () => Promise<string | null>;
    sendWhatsAppTemplateMessage: (params: WhatsAppMessageParams) => Promise<any>;
    onSuccess: () => void;
    onError: (error: Error) => void;
    onFinally: () => void;
}

interface WhatsAppMessageParams {
    to: string[];
    templateName: string;
    templateLanguage: string;
    components?: any[];
    accessToken: string;
    accountPhoneNumberId: string;
    companyId: string;
    displayFields: DisplayFields;
}

/**
 * Sends a WhatsApp template message with optional media
 * @param params All required parameters for sending a template
 * @returns Promise that resolves when the message is sent
 */
export const sendWhatsAppTemplate = async ({
    selectedContact,
    selectedPhoneNumberInfo,
    companyId,
    template,
    inputValues,
    componentsState,
    uploadedMediaId,
    selectedFile,
    accessToken,
    fetchAccessToken,
    sendWhatsAppTemplateMessage,
    onSuccess,
    onError,
    onFinally
}: SendTemplateParams): Promise<void> => {
    console.log("Sending template:", template);

    if (!selectedContact || !selectedPhoneNumberInfo || !companyId) {
        onError(new Error("Missing required parameters"));
        onFinally();
        return;
    }

    try {
        console.log("info", selectedPhoneNumberInfo);
        console.log('selectedContact', selectedContact);

        // Get access token if not already available
        const token = accessToken || await fetchAccessToken();

        if (!token) {
            throw new Error("Could not get access token for WhatsApp API");
        }

        //   selectedContact is an array of leadIds and we need to fetch the phone numbers of these leads
        const phoneNumbers = await Promise.all(
            selectedContact.map(async (leadId) => {
                const leadQuery = query(
                    collection(db, "companies", companyId, "leads"),
                    where(documentId(), "==", leadId)
                );
                const leadSnapshot = await getDocs(leadQuery);
                if (leadSnapshot.empty) {
                    console.error(`No lead found with ID: ${leadId}`);
                    return null;
                }
                const leadDoc = leadSnapshot.docs[0];
                const leadData = leadDoc.data();

                // remove all the special characters like + from the phone number
                const phoneNumber = leadData.number.replace(/[^0-9]/g, "");
                console.log("Phone number:", phoneNumber);
                return phoneNumber;
            })
        );

        console.log("Phone numbers:", phoneNumbers);


        // Prepare the message data
        const messageData = prepareMessageData({
            template,
            selectedContact: phoneNumbers.filter(Boolean) as string[],
            selectedPhoneNumberInfo,
            companyId,
            token,
            componentsState,
            inputValues,
            uploadedMediaId,
            selectedFile
        });

        console.log("Data to send:", messageData);

        // Send the message via the Cloud Function
        const result = await sendWhatsAppTemplateMessage(messageData);

        const { success } = result.data as {
            success: boolean;
            messageId: string;
            cleanId: string;
        };

        if (!success) {
            throw new Error("Failed to send message through WhatsApp API");
        }

        // Call the success callback
        onSuccess();
    } catch (err) {
        console.error("Error sending template message:", err);
        onError(err instanceof Error ? err : new Error("Unknown error sending template message"));
    } finally {
        onFinally();
    }
};

/**
 * Helper function to prepare message data based on template type
 */
function prepareMessageData({
    template,
    selectedContact,
    selectedPhoneNumberInfo,
    companyId,
    token,
    componentsState,
    inputValues,
    uploadedMediaId,
    selectedFile
}: {
    template: WhatsappTemplate;
    selectedContact: string[];
    selectedPhoneNumberInfo: PhoneNumberInfo;
    companyId: string;
    token: string;
    componentsState: ComponentState[];
    inputValues: Record<string, string>;
    uploadedMediaId: string | null;
    selectedFile: File | null;
}): WhatsAppMessageParams {
    let components: any[] = [];

    // Check if the template has a header component with media
    const hasHeaderMedia = template.components[0]?.type === 'HEADER' &&
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.components[0]?.format || '');

    if (hasHeaderMedia && uploadedMediaId) {
        // Handle media templates
        components = prepareMediaComponents(template, uploadedMediaId, selectedFile, componentsState, inputValues);
    } else if (componentsState.length > 0) {
        // Handle text-only templates
        components = prepareTextComponents(template, componentsState, inputValues);
    }

    // Prepare display fields
    const displayFields = {
        headerText: template.components[0]?.text?.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
            return inputValues[`${template.components[0]?.type}_${key}`] || match;
        }) || null,
        body: template.components[1]?.text?.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
            return inputValues[`${template.components[1]?.type}_${key}`] || match;
        }) || '',
        footer: template.components[2] && template.components[2].type === 'FOOTER'
            ? template.components[2].text || ''
            : ''
    };

    return {
        to: selectedContact,
        templateName: template.name,
        templateLanguage: template.language,
        accessToken: token,
        accountPhoneNumberId: selectedPhoneNumberInfo.numberId,
        companyId,
        ...(components.length > 0 ? { components } : {}),
        displayFields
    };
}

/**
 * Prepares components for media templates
 */
function prepareMediaComponents(
    template: WhatsappTemplate,
    uploadedMediaId: string,
    selectedFile: File | null,
    componentsState: ComponentState[],
    inputValues: Record<string, string>
): any[] {
    const mediaFormat = template.components[0]?.format?.toLowerCase();

    // Start with the header component that's always included
    const components = [
        {
            type: 'header',
            parameters: [
                {
                    type: mediaFormat,
                    [mediaFormat as string]:
                        mediaFormat === 'document'
                            ? { id: uploadedMediaId, filename: selectedFile?.name }
                            : { id: uploadedMediaId },
                }
            ]
        }
    ];

    // Process additional components (body, footer, etc.)
    if (componentsState.length > 0) {
        const additionalComponents = ['body', 'footer']
            .map(type => {
                // Find variables belonging to this component type
                const typeVariables = componentsState
                    .filter(component => component && component.type.toLowerCase() === type)
                    .flatMap(component => component.variables || []);

                // Only create component if it has variables
                if (typeVariables.length === 0) {
                    return null;
                }

                return {
                    type,
                    parameters: typeVariables.map((variable) => ({
                        type: 'text',
                        ...(template.parameter_format === 'NAMED'
                            ? { parameter_name: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, '$1') }
                            : {}),
                        text: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
                            return inputValues[`${type.toUpperCase()}_${key}`] || match;
                        })
                    }))
                };
            })
            .filter(Boolean); // Remove null entries for component types with no variables

        // Add additional components to the main components array
        if (additionalComponents.length > 0) {
            if (additionalComponents[0]) {
                components.push(additionalComponents[0]);
            }
        }
    }

    return components;
}

/**
 * Prepares components for text-only templates
 */
function prepareTextComponents(
    template: WhatsappTemplate,
    componentsState: ComponentState[],
    inputValues: Record<string, string>
): any[] {
    return ['header', 'body', 'footer']
        .map(type => {
            // Find variables belonging to this component type
            const typeVariables = componentsState
                .filter(component => component && component.type.toLowerCase() === type)
                .flatMap(component => component.variables || []);

            // Only create component if it has variables
            if (typeVariables.length === 0) {
                return null;
            }

            return {
                type,
                parameters: typeVariables.map((variable: Variable) => ({
                    type: 'text',
                    ...(template.parameter_format === 'NAMED'
                        ? { parameter_name: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, '$1') }
                        : {}),
                    text: variable.fullVar.replace(/{{\s*([^{}]+)\s*}}/g, (match, key) => {
                        return inputValues[`${type.toUpperCase()}_${key}`] || match;
                    })
                }))
            };
        })
        .filter(Boolean); // Remove null entries
}